// Shared helpers for the live-processing status surfaces (wizard polling,
// product-form uploads, grant actions).

// One engine backs every status-poll loop (assets table, product-form
// uploads, migration wizard), so each surface gets the same guards: in-flight
// dedup, a poll budget that resets when onResults reports progress, and a
// page-identity check that discards results from a fetch that outlives a
// Turbolinks navigation instead of applying them to the wrong page.
//
// options:
//   page()             — the element whose identity scopes the loop; null stops it
//   hasPending()       — whether anything is still worth polling
//   url()              — the statuses request URL for this cycle
//   onResults(results) — apply a response; return true to reset the poll budget
//   maxPolls           — budget; onBudgetExhausted() fires once when it runs out
//   interval           — ms between polls (default 3000)
//   immediate          — fire the first poll on start() instead of after interval
export function createStatusPoller(options) {
  var interval = options.interval || 3000
  var timer = null
  var inFlight = false
  var count = 0

  function schedule() {
    timer = setTimeout(poll, interval)
  }

  function poll() {
    timer = null
    var pageEl = options.page()
    if (!pageEl || !options.hasPending()) return

    if (count >= options.maxPolls) {
      if (options.onBudgetExhausted) options.onBudgetExhausted()
      return
    }
    count += 1

    inFlight = true
    fetch(options.url(), { headers: { Accept: "application/json" } })
      .then(function (response) {
        if (!response.ok) throw new Error("statuses request failed")
        return response.json()
      })
      .then(function (results) {
        // Turbolinks replaced the body while this request was in flight —
        // the results belong to a page that is no longer displayed.
        if (options.page() !== pageEl) return
        if (options.onResults(results)) count = 0
      })
      .catch(function () {
        // Transient failure — keep polling within the budget.
      })
      .then(function () {
        inFlight = false
        if (options.page() === pageEl && options.hasPending()) schedule()
      })
  }

  return {
    start: function () {
      count = 0
      if (timer || inFlight) return
      if (options.immediate) poll()
      else schedule()
    },
    stop: function () {
      // A fetch may still be in flight; the page-identity check in poll()
      // discards its results, so clearing the flag here just lets the next
      // page's start() begin a fresh loop.
      inFlight = false
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
    },
    hasPending: function () {
      return options.hasPending()
    },
  }
}

// Default budget-exhausted notice for upload/scan pollers.
export function stillScanningNotice() {
  import("../util/methods").then(function (methods) {
    methods.displayFlashMessage(
      "warning",
      "Some files are still scanning — refresh the page later to see their status.",
      { persist: true }
    )
  })
}

export var SPINNER_HTML =
  '<i class="fas fa-sync fa-spin small me-1" aria-hidden="true"></i>'

// Dynamic text (filenames, scan messages) is never markup.
export function escapeText(text) {
  var probe = document.createElement("div")
  probe.textContent = text
  return probe.innerHTML
}

// Reload to re-render server-side lists — unless the section form has unsaved
// edits, which a reload would silently discard.
export function safeRefresh(message) {
  var dirty = document.querySelector(".js-savebar-dirty")
  if (dirty && !dirty.classList.contains("d-none")) {
    import("../util/methods").then(function (methods) {
      methods.displayFlashMessage(
        "success",
        message + " Save your changes, then reload to see the updated list."
      )
    })
  } else {
    Turbolinks.visit(window.location.toString())
  }
}
